/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Subsets", description = "Subsets restrict which rows of customer data each user can access in Customer Studio, so audiences are automatically scoped to the data a teammate is permitted to see.", lastUpdatedDate = new Date(1791244800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Subsets",
        description: "Subsets restrict which rows of customer data each user can access in Customer Studio, so audiences are automatically scoped to the data a teammate is permitted to see.",
        lastUpdatedDate: "Tue Oct 06 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Subset categories vs. subsets",
          "depth": 1,
          "id": "subset-categories-vs-subsets"
        }, {
          "value": "Set up subsets",
          "depth": 0,
          "id": "set-up-subsets"
        }, {
          "value": "1. Select a parent model",
          "depth": 1,
          "id": "1-select-a-parent-model"
        }, {
          "value": "2. Create a subset category",
          "depth": 1,
          "id": "2-create-a-subset-category"
        }, {
          "value": "3. Decide whether the category is required",
          "depth": 1,
          "id": "3-decide-whether-the-category-is-required"
        }, {
          "value": "4. Add subsets to the category",
          "depth": 1,
          "id": "4-add-subsets-to-the-category"
        }, {
          "value": "5. Grant access to user groups",
          "depth": 1,
          "id": "5-grant-access-to-user-groups"
        }, {
          "value": "How subsets apply to audiences",
          "depth": 0,
          "id": "how-subsets-apply-to-audiences"
        }, {
          "value": "How subsets apply to journeys",
          "depth": 0,
          "id": "how-subsets-apply-to-journeys"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/overview#for-data-teams-and-platform-admins",
                children: "Set up for platform admins and data teams"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Subsets restrict which rows of customer data each user can access in Customer Studio. Admins define subset filters based on attributes like region, brand, or consent, then grant access to user groups. Every audience those users build is automatically scoped to the rows they're permitted to see."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio has several governance controls, each scoped to a different question. Subsets are the row-level control."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "To control…"
            }), _jsx(_components.th, {
              children: "Use"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Which ", _jsx(_components.strong, {
                children: "rows"
              }), " a user can work with"]
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Subsets"
              }), " (this page)"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Which ", _jsx(_components.strong, {
                children: "columns or values"
              }), " are visible or syncable"]
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/data-masking",
                children: "Privacy levels and data masking"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["What can leave to a specific ", _jsx(_components.strong, {
                children: "destination"
              })]
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/destination-rules",
                children: "Destination rules"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Consent"
              }), " enforcement from OneTrust"]
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/onetrust",
                children: "OneTrust Snowflake Native App"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Who can ", _jsx(_components.strong, {
                children: "change"
              }), " schema, traits, or governance"]
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/workspace-management/roles",
                children: "Workspace roles"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use subsets when different teammates should only work with part of your customer data. For example, a company with a global customer table can create a subset per region and grant each region's user group access to its subset. Every audience those users build is then scoped to those rows, so they can't sync customers outside it."
      }), "\n", _jsx(_components.p, {
        children: "Common uses:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), " — Scope each marketer to the geography they work in, so an EMEA marketer never builds against North American records."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Brand or business unit"
          }), " — Give each team access only to the customers of the brand or product line they own."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consent"
          }), " — Scope access by opt-in type, so lifecycle marketers only see email opt-ins and performance marketers only see ad-targetable customers."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "subset-categories-vs-subsets",
        children: "Subset categories vs. subsets"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "parent model"
        }), " is segmented with ", _jsx(_components.strong, {
          children: "subset categories"
        }), " and ", _jsx(_components.strong, {
          children: "subsets"
        }), ". The category is the dimension (for example, ", _jsx(_components.code, {
          children: "Region"
        }), "); each subset is a value within it (", _jsx(_components.code, {
          children: "North America"
        }), ", ", _jsx(_components.code, {
          children: "EMEA"
        }), ", ", _jsx(_components.code, {
          children: "APAC"
        }), ")."]
      }), "\n", _jsxs(_components.p, {
        children: ["When a marketer builds an audience, they can pick more than one category and more than one subset within a category. A customer must match ", _jsx(_components.strong, {
          children: "any"
        }), " subset selected within a category, and satisfy ", _jsx(_components.strong, {
          children: "every"
        }), " category, to qualify for the audience."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-subsets",
        children: "Set up subsets"
      }), "\n", _jsxs(_components.p, {
        children: ["Subsets are configured under ", _jsx(_components.strong, {
          children: "Customer Studio > Governance"
        }), ", on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/governance/subset-categories?from=docs",
          children: [_jsx(_components.strong, {
            children: "Subset categories"
          }), " tab"]
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-add-subset-category.png",
          alt: "Open subset categories"
        })
      }), "\n", _jsx(_components.h3, {
        id: "1-select-a-parent-model",
        children: "1. Select a parent model"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "parent model"
        }), " you want to scope at the top of the page. Subsets apply to audiences built on that model."]
      }), "\n", _jsx(_components.h3, {
        id: "2-create-a-subset-category",
        children: "2. Create a subset category"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Add subset category"
        }), ", name the dimension (for example, ", _jsx(_components.code, {
          children: "Region"
        }), " or ", _jsx(_components.code, {
          children: "Consent"
        }), "), and add an optional description. Click ", _jsx(_components.strong, {
          children: "Create subset category"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-new-category.png",
          alt: "New subset category"
        })
      }), "\n", _jsx(_components.h3, {
        id: "3-decide-whether-the-category-is-required",
        children: "3. Decide whether the category is required"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the category and turn on ", _jsx(_components.strong, {
          children: "Required"
        }), " when a marketer must select a subset from it to build an audience on this parent model. Leave it off when the scoping is optional."]
      }), "\n", _jsx(_components.p, {
        children: "A required category with no accessible subset blocks affected users entirely: if none of a marketer's user groups can access a subset in a required category, they can't build audiences on that parent model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-required.png",
          alt: "Toggle required category"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If audiences already exist on this parent model without a selection for the category, turning on ", _jsx(_components.strong, {
          children: "Required"
        }), " prompts you to choose a subset to backfill them, so those audiences stay valid."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-required-backfill.png",
          alt: "Backfill existing audiences"
        })
      }), "\n", _jsx(_components.h3, {
        id: "4-add-subsets-to-the-category",
        children: "4. Add subsets to the category"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Add subset"
        }), " and name it (for example, ", _jsx(_components.code, {
          children: "Canada"
        }), ", ", _jsx(_components.code, {
          children: "Email opt-in"
        }), "). A subset has two tabs: define which customers it includes on ", _jsx(_components.strong, {
          children: "Audience permissions"
        }), ", and grant access on ", _jsx(_components.strong, {
          children: "User groups"
        }), ". On ", _jsx(_components.strong, {
          children: "Audience permissions"
        }), ", click ", _jsx(_components.strong, {
          children: "Add filter"
        }), " to set membership."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-add-filter.png",
          alt: "New subset form"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You define subset membership in the same visual builder used for audiences, with nested ", _jsx(_components.code, {
          children: "AND"
        }), " / ", _jsx(_components.code, {
          children: "OR"
        }), " logic. For how to build and combine conditions, see ", _jsx(_components.a, {
          href: "/customer-studio/audience-filters",
          children: "Audience filters"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "5-grant-access-to-user-groups",
        children: "5. Grant access to user groups"
      }), "\n", _jsxs(_components.p, {
        children: ["Access is granted by ", _jsx(_components.a, {
          href: "/workspace-management/user-management#groups",
          children: "user group"
        }), ", not by individual user. On the ", _jsx(_components.strong, {
          children: "User groups"
        }), " tab, select the groups whose members should be able to build audiences on this subset, then click ", _jsx(_components.strong, {
          children: "Create subset"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "A group appears in this list only if it already has access to the parent model. Admin groups always have access and can't be deselected."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-user-groups.png",
          alt: "Grant access to user groups"
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-subsets-apply-to-audiences",
        children: "How subsets apply to audiences"
      }), "\n", _jsx(_components.p, {
        children: "When a marketer creates or edits an audience, they see any required categories and can choose the subsets they have access to. Their selections apply as base filters on top of the audience definition, so results never include rows outside the subset."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/cs-subsets-audience-editor.png",
          alt: "Subsets in audience builder"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Because subsets scope what a user can see and build, they're the first place to look when a marketer's audience count is lower than expected. Membership can also be trimmed later by ", _jsx(_components.a, {
          href: "/customer-studio/destination-rules",
          children: "destination rules"
        }), " and consent filters."]
      }), "\n", _jsx(_components.h2, {
        id: "how-subsets-apply-to-journeys",
        children: "How subsets apply to journeys"
      }), "\n", _jsx(_components.p, {
        children: "A journey takes its subsets from its entry:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enters audience"
          }), " — The journey uses the entry audience's subsets."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Performs events"
          }), " — You select subsets on the journey's Start tile. See ", _jsx(_components.a, {
            href: "/customer-studio/journeys/create#subsets",
            children: "Subsets on event entry"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Either way, the selection scopes who enters the journey and who ", _jsx(_components.strong, {
          children: "Send email"
        }), ", ", _jsx(_components.strong, {
          children: "Send SMS"
        }), ", and ", _jsx(_components.strong, {
          children: "Send push"
        }), " tiles can send to. Members already in the journey aren't removed if they stop matching."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/data-masking",
            children: "Data masking"
          }), " to hide or block sensitive columns and values, even for users who can see the rows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "Destination rules"
          }), " to control which records can reach a given destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to see how subset selections apply as base filters."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/create#subsets",
            children: "Create a journey"
          }), " to select subsets on an event-based Start tile."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
